<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useDebounceFn } from '@vueuse/core'
import { useTranslation } from 'i18next-vue'
import { useAppStore } from '@/stores/AppStore'
import { useAppointmentRepository } from '@/repositories/appointmentRepo'
import type { DoctorWorkSchedule } from '@/type/Appointment'
import { getDoctorDisplayNamei18n } from '@/utils/doctor'
import { themeColor } from '@/theme'
import AppointmentDailyDoctorCard from './AppointmentDailyDoctorCard.vue'

const { t } = useTranslation()

interface Props {
  branchUuid: string | null
  date: Date | null
  display_limit?: number
}

const props = withDefaults(defineProps<Props>(), {
  display_limit: 5,
})

const appStore = useAppStore()
const appointmentRepo = useAppointmentRepository()

const accentColor = computed(() =>
  appStore.isXpress ? themeColor.xpress.primary2 : themeColor.aura.primary2,
)

const doctors = ref<DoctorWorkSchedule[]>([])
const loading = ref(false)
const showAllDoctors = ref(false)

const displayedDoctors = computed(() =>
  showAllDoctors.value ? doctors.value : doctors.value.slice(0, props.display_limit),
)
const hasMoreDoctors = computed(() => doctors.value.length > props.display_limit)
const hiddenCount = computed(() => doctors.value.length - props.display_limit)

function toggleShowAllDoctors() {
  showAllDoctors.value = !showAllDoctors.value
}

function formatTime(dateTimeString: string): string {
  if (!dateTimeString) return ''
  try {
    const date = new Date(dateTimeString)
    if (isNaN(date.getTime())) return dateTimeString
    const hours = date.getHours().toString().padStart(2, '0')
    const minutes = date.getMinutes().toString().padStart(2, '0')
    return `${hours}:${minutes}`
  } catch (error) {
    console.error('Error formatting time:', error)
    return dateTimeString
  }
}

async function fetchDoctors() {
  if (!props.branchUuid || !props.date) {
    doctors.value = []
    loading.value = false
    return
  }

  try {
    loading.value = true

    const start = new Date(props.date)
    start.setHours(0, 0, 0, 0)
    const end = new Date(props.date)
    end.setHours(23, 59, 59, 999)

    const data = await appointmentRepo.fetchDailyDoctorWork({
      clinicUuid: appStore.clinicUUID,
      branchUuid: props.branchUuid,
      start,
      end,
    })

    // Filter unique doctors by doctor_id
        doctors.value = data.filter(
      (doctor: DoctorWorkSchedule, index: number, self: DoctorWorkSchedule[]) =>
        index === self.findIndex((d: DoctorWorkSchedule) => d.doctor_id === doctor.doctor_id),
    )
    showAllDoctors.value = false
  } catch (error) {
    console.error('Error fetching doctor work schedule:', error)
    doctors.value = []
  } finally {
    loading.value = false
  }
}

const debouncedFetchDoctors = useDebounceFn(fetchDoctors, 500)

watch([() => props.branchUuid, () => props.date], () => {
  if (!props.branchUuid || !props.date) {
    doctors.value = []
    loading.value = false
    return
  }
  loading.value = true
  debouncedFetchDoctors()
})
</script>

<template>
  <div v-if="branchUuid && date" class="tw-px-3 tw-min-h-[80px]">
    <!-- Type 1: no existing doctors, show text -->
    <div v-if="loading && doctors.length === 0" class="tw-mt-4">
      <p class="tw-text-center">{{ t('appointment.loading_doctors') }}</p>
    </div>

    <!-- Doctors list, stays mounted while refreshing -->
    <Transition name="wipe-up" mode="out-in">
      <div v-if="doctors.length > 0" class="tw-relative">
        <!-- Type 2: overlay spinner when refreshing existing list -->
        <Transition name="fade">
          <div
            v-if="loading"
            class="tw-absolute tw-inset-0 tw-z-10 tw-flex tw-items-center tw-justify-center tw-rounded-xl tw-bg-white/60"
          >
            <van-loading type="spinner" size="32" />
          </div>
        </Transition>

        <p class="tw-text-lg tw-font-bold tw-mb-2" :style="{ color: accentColor }">
          {{ t('appointment.daily_doctors') }}
        </p>
        <TransitionGroup name="wipe-up-item" tag="div">
          <AppointmentDailyDoctorCard
            v-for="(doctor, index) in displayedDoctors"
            :key="doctor.doctor_id"
            :doctor-name="
              getDoctorDisplayNamei18n(doctor, appStore.language, t('appointment.unnamed_doctor'))
            "
            :start-time="formatTime(doctor.start)"
            :end-time="formatTime(doctor.end)"
            :breaks="doctor.breaks"
            :style="{ transitionDelay: `${index * 50}ms` }"
          />
        </TransitionGroup>

        <div v-if="hasMoreDoctors" class="tw-text-center tw-mt-2">
          <van-button
            type="primary"
            size="small"
            round
            :style="{ backgroundColor: accentColor, borderColor: accentColor }"
            @click="toggleShowAllDoctors"
          >
            {{
              showAllDoctors
                ? t('appointment.show_less_doctors')
                : t('appointment.show_more_doctors', { doctors: hiddenCount })
            }}
          </van-button>
        </div>
      </div>
    </Transition>
  </div>
</template>

<style scoped>
/* Wipe-up for the overall container */
.wipe-up-enter-active,
.wipe-up-leave-active {
  transition: all 0.3s ease;
}

.wipe-up-enter-from {
  opacity: 0;
  transform: translateY(20px);
}
.wipe-up-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* Fade for overlay spinner */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
